import React from 'react';

function daysUntil(iso) {
  if (!iso) return null;
  var d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  var t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
}

// Returns null if no monitoring data, else {down, ssl, dom, severity}
export function monitorSummary(site) {
  if (!site) return null;
  var down = site.monitor_status === 'down';
  var ssl = daysUntil(site.ssl_expires_at);
  var dom = daysUntil(site.domain_expires_at);
  if (!down && ssl == null && dom == null && !site.monitor_status) return null;

  var severity = 'green';
  var worst = Math.min(ssl == null ? 9999 : ssl, dom == null ? 9999 : dom);
  if (down) severity = 'red';
  else if (worst <= 7) severity = 'red';
  else if (worst <= 30) severity = 'yellow';
  return { down: down, ssl: ssl, dom: dom, severity: severity };
}

var COLORS = { green: 'var(--green)', yellow: 'var(--orange)', red: 'var(--red)' };

// Compact badge for the sites table
export function MonitorBadge({ site }) {
  var m = monitorSummary(site);
  if (!m) return React.createElement('span', { style: { color: 'var(--text-muted)', fontSize: '12px' } }, '-');
  var parts = [];
  if (m.ssl != null) parts.push('SSL ' + m.ssl + ' ימים');
  if (m.dom != null) parts.push('דומיין ' + m.dom + ' ימים');
  var text = m.down ? 'לא זמין' : (parts.length ? parts.join(' · ') : 'תקין');
  // Hover shows the exact expiry dates (same as the site-level card)
  var tip = [];
  if (m.down) tip.push('האתר אינו זמין');
  if (site.ssl_expires_at) tip.push('תוקף SSL: ' + fmtDate(site.ssl_expires_at));
  if (site.domain_expires_at) tip.push('תוקף דומיין: ' + fmtDate(site.domain_expires_at));
  return React.createElement('span', {
    title: tip.join(' · '),
    style: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: COLORS[m.severity], whiteSpace: 'nowrap' },
  },
    React.createElement('span', { style: { width: '8px', height: '8px', borderRadius: '50%', background: COLORS[m.severity], display: 'inline-block', flexShrink: 0 } }),
    text
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  var d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('he-IL');
}

// Always-visible monitoring detail card for a site page
export function MonitorDetail({ site }) {
  var m = monitorSummary(site);
  if (!m) return null;
  var col = COLORS[m.severity];
  function line(label, days, iso) {
    var c = days == null ? 'var(--text-muted)' : (days <= 7 ? COLORS.red : days <= 30 ? COLORS.yellow : COLORS.green);
    var suffix = days != null ? (' (' + days + ' ימים)') : '';
    return React.createElement('span', { style: { color: c } }, label + ': ' + fmtDate(iso) + suffix);
  }
  return React.createElement('div', {
    style: {
      padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
      border: '1px solid ' + col,
      background: m.severity === 'green' ? 'var(--surface)' : (m.severity === 'red' ? 'rgba(239,68,68,0.10)' : 'rgba(245,158,11,0.10)'),
      display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center', fontSize: '13px',
    },
  },
    React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 700, color: col } },
      React.createElement('span', { style: { width: '9px', height: '9px', borderRadius: '50%', background: col, display: 'inline-block' } }),
      'ניטור · ' + (m.down ? 'האתר אינו זמין' : 'פעיל')
    ),
    line('SSL', m.ssl, site.ssl_expires_at),
    line('דומיין', m.dom, site.domain_expires_at),
    site.monitor_checked_at ? React.createElement('span', { style: { color: 'var(--text-muted)', marginInlineStart: 'auto' } }, 'נבדק: ' + fmtDate(site.monitor_checked_at)) : null
  );
}

// Banner shown on a site page when something needs attention
export function MonitorBanner({ site }) {
  var m = monitorSummary(site);
  if (!m || m.severity === 'green') return null;
  var msgs = [];
  if (m.down) msgs.push('האתר אינו זמין כעת');
  if (m.ssl != null && m.ssl <= 30) msgs.push('תעודת SSL פגה בעוד ' + m.ssl + ' ימים');
  if (m.dom != null && m.dom <= 30) msgs.push('הדומיין פג בעוד ' + m.dom + ' ימים');
  if (!msgs.length) return null;
  var col = COLORS[m.severity];
  return React.createElement('div', {
    style: {
      padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
      background: m.severity === 'red' ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
      color: col, border: '1px solid ' + col, fontSize: '14px', fontWeight: 600,
    },
  }, '⚠️ ' + msgs.join(' · '));
}
